import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { ReadDocumentationButton } from '@/Domain/components/Integration/components/ReadDocumentationButton';

interface IntegrationIntroductionProps {
  description: ReactNode;
  /** Concise checklist of what's needed before connecting. Omit when there is nothing to prepare. */
  prerequisites?: ReactNode[];
  /** Omit for flows with no docs page of their own. */
  docsHref?: string;
}

/**
 * Section 1 — "About": a short description of what the integration does, an
 * optional Prerequisites checklist, and the single Read documentation button.
 */
export const IntegrationIntroduction = ({
  description,
  prerequisites,
  docsHref,
}: IntegrationIntroductionProps) => {
  const { t } = useTranslation('integrations');

  return (
    <IntegrationSection
      title={t('integrations:section.introduction')}
      data-testid="integration-introduction"
    >
      <p className="m-0 pb-2 text-sm text-muted-foreground text-pretty">{description}</p>
      {prerequisites?.length ? (
        <div className="space-y-1.5" data-testid="integration-prerequisites">
          <p className="text-sm font-medium text-foreground">
            {t('integrations:section.prerequisites')}
          </p>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground marker:text-foreground-subtle">
            {prerequisites.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {docsHref ? <ReadDocumentationButton href={docsHref} /> : null}
    </IntegrationSection>
  );
};

export default IntegrationIntroduction;
